/** Retention, churn risk and lost / recovered customers (`acp.ret|churn|lr`). `acpRiskEn` is typed against the Polish shape. */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const acpRiskPl = {
  ret: {
    kpiSection: 'Wskaźniki retencji',
    month: 'Miesiąc',
    baseline: 'baza',
    baselineHint:
      'Najstarsza kohorta: pierwsza faktura mogła być wcześniejsza niż dane — to baza klientów, nie pozyskanie.',
    kpi: {
      acquired: 'Pozyskani klienci',
      acquiredHint: 'w kohortach (bez bazy)',
      after: 'Retencja po {n} mies.',
      hintCustomers: 'średnia ważona, % klientów',
      hintRevenue: 'średnia ważona, % przychodu z M0',
    },
    metric: { customers: 'Klienci', revenue: 'Przychód' },
    heat: {
      title: 'Kohorty pozyskania',
      subtitle:
        'Wiersz = miesiąc pierwszej faktury, kolumna = miesięcy od pozyskania. Ciemniej = więcej zostało.',
      cohort: 'Kohorta',
      size: '{n} klientów',
      average: 'Średnia',
    },
    curve: { title: 'Średnia krzywa retencji', subtitle: 'Średnia ważona rozmiarem kohort' },
    tip: {
      cohort: 'Kohorta {month} · {n} klientów',
      month: 'Miesiąc {n} od pozyskania ({cal})',
      customers: 'Klienci',
      revenue: 'Przychód',
      ofM0: 'przychodu z M0',
    },
    footnote: 'Stan na {month}. Kohorty bez klientów pominięto; puste pola = miesiąc jeszcze nie nastąpił.',
  },
  churn: {
    bandsSection: 'Pasma ryzyka',
    customersWord: 'klientów',
    band: { LOW: 'Niskie', MEDIUM: 'Średnie', HIGH: 'Wysokie' },
    factor: {
      orderGap: 'Przerwa w zamówieniach',
      salesTrend: 'Spadek sprzedaży',
      marginTrend: 'Spadek marży',
      visitRecency: 'Dawno bez wizyty',
      overdueReceivables: 'Należności po terminie',
    },
    tile: { atStake: 'Sprzedaż 12 mies. „na stole”', share: 'Udział w sprzedaży' },
    weights: {
      title: 'Z czego składa się wynik (0–100)',
      subtitle: 'Pięć ważonych czynników · oceniani aktywni klienci: {n}',
    },
    table: {
      title: 'Klienci o najwyższym ryzyku odejścia',
      titleBand: 'Ryzyko: {band}',
      subtitle: 'Pokazano {shown} z {total} · kliknij wiersz, by zobaczyć, dlaczego',
      none: 'Brak klientów w tym paśmie ryzyka.',
      risk: 'Ryzyko',
      why: 'Dlaczego',
      whyTitle: 'Wynik {score}/100 — z czego się składa',
      weight: 'waga {w}',
      action: 'Sugerowane działanie',
      sales12: 'Sprzedaż 12 mies.',
      sinceOrder: 'Od zamówienia',
      typicalShort: 'zwykle co {n} d',
      trend: 'Sprzedaż 60 d',
      lastOrder: 'Ostatnie zamówienie',
      typical: 'Typowy odstęp',
      overdue: 'Należności po terminie',
      visit: 'Od ostatniej wizyty',
      expand: 'Rozwiń szczegóły',
      collapse: 'Zwiń szczegóły',
      more: 'Pokaż więcej (do {n})',
    },
  },
  lr: {
    showAll: 'Pokaż wszystkie ({n})',
    showLess: 'Pokaż mniej',
    truncated: 'Lista obejmuje {shown} największych z {total}.',
    col: {
      class: 'Klasa',
      lostAt: 'Utracony',
      daysSince: 'Bez zamówienia',
      turnover: 'Utracony obrót',
      recoveredAt: 'Wrócił',
      gap: 'Przerwa',
      reactivation: 'Wartość powrotu',
      before: 'obrót przed: {v}',
    },
    lost: {
      title: 'Utraceni klienci',
      subtitle:
        'Brak zakupu od ponad {days} dni (próg z Ustawień) · utracony obrót = sprzedaż 12 mies. do ostatniego zakupu',
      none: 'Brak utraconych klientów w tym okresie.',
      count: 'Utraceni',
      turnover: 'Utracony obrót',
      classA: 'w tym klasa A',
      avgDays: 'Śr. dni bez zam.',
      recoveredTag: 'wrócił',
    },
    rec: {
      title: 'Odzyskani klienci',
      subtitle: 'Zamówili po przerwie · wartość powrotu = zamówienie z dnia powrotu',
      none: 'Brak odzyskanych klientów w tym okresie.',
      count: 'Odzyskani',
      value: 'Wartość powrotów',
      avgGap: 'Śr. przerwa',
      turnoverBefore: 'Obrót sprzed przerwy',
    },
  },
};

export type AcpRisk = typeof acpRiskPl;

export const acpRiskEn: Shape<AcpRisk> = {
  ret: {
    kpiSection: 'Retention indicators',
    month: 'Month',
    baseline: 'base',
    baselineHint:
      'Oldest cohort: the first invoice may predate the data — this is the customer base, not an acquisition.',
    kpi: {
      acquired: 'Acquired customers',
      acquiredHint: 'in cohorts (excl. base)',
      after: 'Retention after {n} mo.',
      hintCustomers: 'weighted average, % of customers',
      hintRevenue: 'weighted average, % of M0 revenue',
    },
    metric: { customers: 'Customers', revenue: 'Revenue' },
    heat: {
      title: 'Acquisition cohorts',
      subtitle:
        'Row = month of the first invoice, column = months since acquisition. Darker = more retained.',
      cohort: 'Cohort',
      size: '{n} customers',
      average: 'Average',
    },
    curve: { title: 'Average retention curve', subtitle: 'Weighted by cohort size' },
    tip: {
      cohort: 'Cohort {month} · {n} customers',
      month: 'Month {n} since acquisition ({cal})',
      customers: 'Customers',
      revenue: 'Revenue',
      ofM0: 'of M0 revenue',
    },
    footnote: 'As of {month}. Empty cohorts are omitted; blank cells = the month has not happened yet.',
  },
  churn: {
    bandsSection: 'Risk bands',
    customersWord: 'customers',
    band: { LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High' },
    factor: {
      orderGap: 'Order gap',
      salesTrend: 'Sales decline',
      marginTrend: 'Margin decline',
      visitRecency: 'No recent visit',
      overdueReceivables: 'Overdue receivables',
    },
    tile: { atStake: '12-month sales “at stake”', share: 'Share of sales' },
    weights: {
      title: 'What the score is made of (0–100)',
      subtitle: 'Five weighted factors · active customers scored: {n}',
    },
    table: {
      title: 'Customers most likely to leave',
      titleBand: 'Risk: {band}',
      subtitle: 'Showing {shown} of {total} · click a row to see why',
      none: 'No customers in this risk band.',
      risk: 'Risk',
      why: 'Why',
      whyTitle: 'Score {score}/100 — what it is made of',
      weight: 'weight {w}',
      action: 'Suggested action',
      sales12: 'Sales 12 mo.',
      sinceOrder: 'Since order',
      typicalShort: 'usually every {n} d',
      trend: 'Sales 60 d',
      lastOrder: 'Last order',
      typical: 'Typical interval',
      overdue: 'Overdue receivables',
      visit: 'Since last visit',
      expand: 'Show details',
      collapse: 'Hide details',
      more: 'Show more (up to {n})',
    },
  },
  lr: {
    showAll: 'Show all ({n})',
    showLess: 'Show less',
    truncated: 'List covers the {shown} largest of {total}.',
    col: {
      class: 'Class',
      lostAt: 'Lost on',
      daysSince: 'No order for',
      turnover: 'Lost turnover',
      recoveredAt: 'Came back',
      gap: 'Gap',
      reactivation: 'Comeback value',
      before: 'turnover before: {v}',
    },
    lost: {
      title: 'Lost customers',
      subtitle: 'No purchase for over {days} days (threshold in Settings) · lost turnover = 12-month sales up to the last purchase',
      none: 'No lost customers in this period.',
      count: 'Lost',
      turnover: 'Lost turnover',
      classA: 'of which class A',
      avgDays: 'Avg days w/o order',
      recoveredTag: 'came back',
    },
    rec: {
      title: 'Recovered customers',
      subtitle: 'Ordered after a gap · comeback value = the order on the day they returned',
      none: 'No recovered customers in this period.',
      count: 'Recovered',
      value: 'Comeback value',
      avgGap: 'Avg gap',
      turnoverBefore: 'Turnover before the gap',
    },
  },
};
